import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { type IIconsVector } from 'shared/ui/Icon/icons'
import { isIconKey } from 'shared/ui'
import { EnumReviewAccount, type IReviewAccount, type IReviewAccountType } from '../api/types'

const REVIEW_ACCOUNT_ICONS: Record<IReviewAccountType, IIconsVector> = {
  google: 'google',
  facebook: 'serviceFacebook',
  yelp: 'serviceYelp',
  bbb: 'serviceBBB',
  angi: 'serviceAngi',
  capterra: 'serviceCapterra',
  g2: 'serviceG2',
  custom: 'solidLink',
}

export class ReviewAccount {
  readonly _id: string = nanoid()
  private _apiId: number | null = null
  private _type: IReviewAccountType = 'custom'
  private _urlLink = ''
  private _buttonName = ''
  private _sortOrder = 0
  private _buttonNameError = ''
  private _urlLinkError = ''

  constructor(item?: IReviewAccount) {
    if (item) this.syncOrigin(item)

    makeAutoObservable(this)
  }

  syncOrigin = (origin: IReviewAccount) => {
    if (origin.id) this._apiId = origin.id
    this._type = origin.type
    this._urlLink = origin.url_link
    this._buttonName = origin.button_name
    this._sortOrder = origin.sort_order ?? 0
  }

  setType = (value: IReviewAccountType) => {
    this._type = value
  }

  setUrl = (value: string) => {
    this._urlLink = value
  }

  setButtonName = (value: string) => {
    this._buttonName = value
  }

  setSortOrder = (order: number) => {
    this._sortOrder = order
  }

  setButtonNameError = (value: string) => {
    this._buttonNameError = value
  }

  setUrlLinkError = (value: string) => {
    this._urlLinkError = value
  }

  get key() {
    return this._id
  }

  get icon(): IIconsVector {
    const icon = REVIEW_ACCOUNT_ICONS[this._type]

    return isIconKey(icon) ? icon : 'solidLink'
  }

  get name() {
    return EnumReviewAccount[this._type]
  }

  get customName() {
    return this._buttonName ? this._buttonName : this.name
  }

  get type() {
    return this._type
  }

  get url() {
    return this._urlLink
  }

  get buttonName() {
    return this._buttonName
  }

  get sortOrder() {
    return this._sortOrder
  }

  get buttonNameError() {
    return this._buttonNameError
  }

  get urlLinkError() {
    return this._urlLinkError
  }

  get params(): IReviewAccount {
    return {
      ...(this._apiId !== null && { id: this._apiId }),
      type: this._type,
      url_link: this._urlLink.trim(),
      button_name: this._buttonName.trim(),
      sort_order: this._sortOrder,
    }
  }
}
